import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Clock, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  technique: { label: 'Technique', color: 'bg-blue-100 text-blue-700' },
  training: { label: 'Training', color: 'bg-green-100 text-green-700' },
  strategy: { label: 'Strategy', color: 'bg-purple-100 text-purple-700' },
  fitness: { label: 'Fitness', color: 'bg-orange-100 text-orange-700' },
  gear: { label: 'Gear', color: 'bg-pink-100 text-pink-700' }
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [activeCategory]);

  const fetchArticles = async () => {
    try {
      const params = activeCategory ? `?category=${activeCategory}` : '';
      const res = await axios.get(`${API}/blog/${params}`);
      setArticles(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/blog/categories`);
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-page">
      {/* Nav */}
      <nav className="w-full bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_blog-56/artifacts/ar7lcvoq_WhatsApp%20Image%202026-02-21%20at%2022.08.34.jpeg" alt="Logo" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold text-blue-600">Badminton Stats</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/features" className="text-sm text-gray-600 hover:text-blue-600 hidden sm:inline">Features</Link>
            <Link to="/leaderboard" className="text-sm text-gray-600 hover:text-blue-600 hidden sm:inline">Leaderboard</Link>
            <Link to="/blog" className="text-sm font-medium text-blue-600 hidden sm:inline">Blog</Link>
            <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="blog-signup-btn">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-3">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-1">
            <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium">Blog</li>
          </ol>
        </nav>
      </div>

      {/* Hero */}
      <header className="max-w-4xl mx-auto px-6 pt-6 pb-10 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
          <BookOpen className="h-9 w-9 text-blue-600" /> Badminton Tips & Guides
        </h1>
        <p className="text-base text-gray-600 max-w-2xl mx-auto">
          Expert advice on technique, strategy, fitness, and gear to help you improve your badminton game. Written by coaches and experienced players.
        </p>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-20">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!activeCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid="blog-filter-all"
          >
            All
          </button>
          {categories.map(cat => {
            const catInfo = CATEGORY_LABELS[cat] || { label: cat, color: 'bg-gray-100 text-gray-700' };
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`blog-filter-${cat}`}
              >
                {catInfo.label}
              </button>
            );
          })}
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No articles found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map(article => {
              const catInfo = CATEGORY_LABELS[article.category] || { label: article.category, color: 'bg-gray-100 text-gray-700' };
              return (
                <Link to={`/blog/${article.slug}`} key={article.id} className="group" data-testid={`blog-card-${article.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-all group-hover:border-blue-200">
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={`${catInfo.color} text-[10px]`}>{catInfo.label}</Badge>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {article.read_time} min read
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-sm text-gray-600 flex-1 line-clamp-3">{article.excerpt}</p>
                      <div className="flex items-center gap-1 mt-4 text-sm text-blue-600 font-medium">
                        Read more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-10 text-white">
          <h2 className="text-2xl font-bold mb-2">Put these tips into practice</h2>
          <p className="text-blue-100 mb-5 max-w-md mx-auto">Track your matches, log your training sessions, and get AI coaching feedback — all free.</p>
          <Button onClick={() => navigate('/signup')} className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2.5 font-semibold">
            Create Free Account
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-6">
            <div>
              <Link to="/" className="text-lg font-bold text-blue-600">Badminton Stats</Link>
              <p className="text-sm text-gray-500 mt-2">AI-powered match tracker and coaching for badminton players.</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Pages</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="text-gray-500 hover:text-blue-600">Features</Link></li>
                <li><Link to="/leaderboard" className="text-gray-500 hover:text-blue-600">Leaderboard</Link></li>
                <li><Link to="/blog" className="text-gray-500 hover:text-blue-600">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-500 hover:text-blue-600">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-500 hover:text-blue-600">Terms & Conditions</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4 text-center text-sm text-gray-500">&copy; 2026 Badminton Stats. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
